import React, { useContext, useState, useEffect, useCallback } from "react";
import { UserContext } from "../contexts/UserContext";
import VerbiDrawer from "./VerbiDrawer";
import { getVerbi } from "../services/verbi";
import verbi_bird from "../assets/verbi_bird.png";
import Button from "./Button";
import "./VerbiWidget.css";
import { VerbiResponse } from "../services/types";

interface VerbiWidgetProps {
    postId: number;
    postVerbi: VerbiResponse;
}

const VerbiWidget: React.FC<VerbiWidgetProps> = ({
    postId,
    postVerbi
}) => {
    const { currentUser } = useContext(UserContext);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [verbiData, setVerbiData] = useState<VerbiResponse | null>(postVerbi);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchVerbiData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getVerbi(postId);
            setVerbiData(response);
        } catch (err) {
            setError("Erreur lors du chargement des données Verbi");
            console.error("Failed to fetch verbi data:", err);
        } finally {
            setLoading(false);
        }
    }, [postId]);

    const handleAnswerClick = () => {
        setIsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const handleVerbiUpdate = () => {
        fetchVerbiData();
    };

    // Don't render if this post doesn't have a verbi
    if (!loading && (!verbiData || verbiData.verbi === false)) {
        return null;
    }

    const foundCount = verbiData?.winners?.length || 0;
    const wordLength = verbiData?.verbi ? verbiData.verbi.length : 0;
    const userHasWon = currentUser && verbiData?.winners?.some(winner => String(winner.id) === String(currentUser.id));

    return (
        <div className="VerbiWidget">
            <div className="VerbiWidget-content">
                <img 
                    src={verbi_bird} 
                    alt="Verbi Bird" 
                    className="VerbiWidget-bird"
                />
                <div className="VerbiWidget-info">
                    {loading && <p>Chargement...</p>}
                    {error && <p className="VerbiWidget-error">{error}</p>}
                    {userHasWon ? (
                        <p className="VerbiWidget-success">
                            Bravo ! La solution est "<strong>{verbiData?.verbi}</strong>".
                        </p>
                    ) : (
                        <p className="VerbiWidget-challenge">
                            Cette BD cache un mot de <strong>{wordLength} lettres</strong> à trouver.
                        </p>
                    )}
                    <p className="VerbiWidget-stats">
                        {foundCount} {foundCount === 1 ? 'personne a' : 'personnes ont'} déjà trouvé la solution.
                    </p>
                    {userHasWon ? (
                        <Button
                            variant="success"
                            size="medium"
                            onClick={handleAnswerClick}
                        >
                            Voir les gagnants
                        </Button>
                    ) : (
                        <>
                        {currentUser ? (
                            <Button 
                                variant="primary"
                                size="medium"
                                onClick={handleAnswerClick}
                            >
                                Répondre
                            </Button>
                        ) : (
                            <p className="VerbiWidget-loginPrompt">
                                Connectez-vous pour participer
                            </p>
                        )}
                        </>
                    )}
                </div>
            </div>

            <VerbiDrawer
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
                postId={postId}
                onVerbiUpdate={handleVerbiUpdate}
                wordLength={wordLength}
                verbiSolution={verbiData?.verbi !== false ? verbiData?.verbi : undefined}
                userHasWon={userHasWon || false}
            />
        </div>
    );
};

export default VerbiWidget;
